import React, { useCallback, useMemo, useState } from "react";
import { CloudscapeJsonForms } from "@amzn/fin-tech-cloudscape-json-forms";
import type { CloudscapeJsonSchema } from "@amzn/fin-tech-cloudscape-json-forms";
import {
  Alert,
  Box,
  Button,
  Container,
  ContentLayout,
  Header,
  KeyValuePairs,
  SpaceBetween,
  Spinner,
} from "@amzn/awsui-components-console";
import type { DocumentType } from "@aws-sdk/types";

import { createClaim } from "src/api/schemaClaims/schemaClaimsService";
import type { ProcessedApiResponse } from "src/api/types";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { buildClaimOutputPairs } from "./claimOutputDisplay";
import {
  clearSchemaFetchState,
  markSubmissionCompleted,
  resetFormData,
  resetPreScreen,
  setFormData,
} from "./jsonFormsSlice";

type SubmissionStatus = "idle" | "submitting" | "success" | "error";

interface SubmissionError {
  message: string;
  errors?: {
    errorCode?: string;
    errorMessage?: string;
    fieldName?: string;
    fieldValue?: string;
  }[];
}

interface SchemaFormWithSubmissionProps {
  fetchedSchema: CloudscapeJsonSchema;
  resolvedSchemaName: string | null;
  /**
   * Pre-screen selections merged into both the displayed form state and
   * the submitted `claimInput` (for example Cargo's `businessUnit`).
   * Built by the active LOB's `buildClaimContext`. Always wins over any
   * colliding persisted/user-entered form field so the displayed value
   * matches what gets submitted.
   */
  preScreenClaimContext?: Record<string, unknown>;
}

/**
 * Derives the Redux `formData` key from the resolved schema name.
 * Falls back to a generic key when no name is available (should not
 * happen in practice since the component is only mounted after a
 * successful schema fetch).
 */
function formDataKey(resolvedSchemaName: string | null): string {
  return resolvedSchemaName ?? "unknown_schema";
}

/**
 * Renders the CloudscapeJsonForms component with submission handling.
 * Manages local submission state (idle/submitting/success/error) and
 * displays inline error alerts matching the pattern used by the
 * existing claim intake flow.
 *
 * Form data is tracked locally for fast re-renders and synced to the
 * Redux `jsonForms.formData` slice on every change so it survives:
 * - Error → Back to edit transitions (local state)
 * - Full page reloads (Redux persisted via redux-remember)
 */
function SchemaFormWithSubmission({
  fetchedSchema,
  resolvedSchemaName,
  preScreenClaimContext,
}: SchemaFormWithSubmissionProps): JSX.Element {
  const dispatch = useAppDispatch();
  const key = formDataKey(resolvedSchemaName);
  const persistedData = useAppSelector((state) => state.jsonForms.formData[key]);
  const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus>("idle");
  const [submissionError, setSubmissionError] = useState<SubmissionError | null>(null);
  // `/createClaim` response held so the confirmation screen can show the
  // returned claim details (claim number, timestamps, ...). The shape is
  // driven by the backend's untyped `claimOutput` document, so it is
  // rendered generically via `buildClaimOutputPairs`.
  const [submittedResponse, setSubmittedResponse] = useState<ProcessedApiResponse | null>(null);

  // Local mirror of form data. Seeded from Redux (which may have been
  // rehydrated from localStorage) so the user picks up where they left
  // off after a page reload. Pre-screen context is spread last so
  // authoritative values like `businessUnit` always win — matching the
  // precedence in handleSubmit where preScreenClaimContext also
  // overrides form data on submission.
  const [formState, setFormState] = useState<Record<string, unknown>>(() => ({
    ...persistedData,
    ...preScreenClaimContext,
  }));

  // Deep-clone to avoid "object is not extensible" errors from AJV
  // trying to annotate the frozen Redux state schema.
  const schemaClone = useMemo(
    () => JSON.parse(JSON.stringify(fetchedSchema)) as CloudscapeJsonSchema,
    [fetchedSchema]
  );

  const handleChange = useCallback(
    (data: Record<string, unknown>) => {
      setFormState(data);
      dispatch(setFormData({ key, data }));
    },
    [dispatch, key]
  );

  const handleSubmit = useCallback(
    (data: Record<string, unknown>) => {
      setSubmissionStatus("submitting");
      setSubmissionError(null);

      createClaim({
        schemaId: resolvedSchemaName ?? "",
        claimInput: {
          ...data,
          ...preScreenClaimContext,
        } as unknown as DocumentType,
        testRecord: true,
      })
        .then((response) => {
          setSubmittedResponse(response);
          setSubmissionStatus("success");
          dispatch(resetFormData({ key }));
          dispatch(markSubmissionCompleted());
        })
        .catch((error) => {
          setSubmissionStatus("error");
          const errors = error?.errors ?? [];
          const message = error?.message ?? "An error occurred while submitting the claim.";
          setSubmissionError({ message, errors });
        });
    },
    [resolvedSchemaName, dispatch, key, preScreenClaimContext]
  );

  const handleBackToEdit = useCallback(() => {
    setSubmissionStatus("idle");
    setSubmissionError(null);
  }, []);

  const handleStartNewForm = useCallback(() => {
    dispatch(resetFormData({ key }));
    dispatch(clearSchemaFetchState());
  }, [dispatch, key]);

  /**
   * Starts a fresh claim from the confirmation screen. Unlike
   * `handleStartNewForm` (which keeps the captured pre-screen answers so the
   * user can retry a failed schema fetch), a new claim must re-run the
   * pre-screen from the top — the previous answers describe the claim that was
   * just submitted. `resetPreScreen` also clears the `submissionCompleted`
   * flag, so the orchestrator drops back to the LOB pre-screen on the next
   * render and this component unmounts.
   */
  const handleStartNewClaim = useCallback(() => {
    setSubmissionStatus("idle");
    setSubmittedResponse(null);
    setFormState({ ...preScreenClaimContext });
    dispatch(resetFormData({ key }));
    dispatch(resetPreScreen());
  }, [dispatch, key, preScreenClaimContext]);

  if (submissionStatus === "submitting") {
    return (
      <Box textAlign="center">
        <SpaceBetween size="xxl" direction="vertical">
          <Alert header="Loading..." data-testid="claim-submitted-loading-alert">
            <span role="alert">
              Thank you for submitting the claim. Please wait on this screen while we process your
              submission.
            </span>
          </Alert>
          <Spinner data-testid="claim-submitted-spinner" size="large" />
        </SpaceBetween>
      </Box>
    );
  }

  if (submissionStatus === "error" && submissionError) {
    const errorItems = submissionError.errors?.length
      ? submissionError.errors.map((err) => ({
          label: err.fieldName ?? "Error",
          value: `${err.errorMessage ?? "Unknown error"}${
            err.fieldValue ? ` (value: ${err.fieldValue})` : ""
          }`,
        }))
      : [{ label: "Error", value: submissionError.message }];

    return (
      <Box textAlign="center">
        <SpaceBetween size="xxl" direction="vertical">
          <Alert type="error" header="Error" data-testid="claim-submitted-error-alert">
            <div role="alert">
              Sorry, your claim submission was unsuccessful. Please correct the errors shown below
              and resubmit the form.
            </div>
          </Alert>
          <Container
            data-testid="claim-submitted-error-data-grid"
            header={
              <Header variant="h2" data-testid="data-grid-header">
                Error Details
              </Header>
            }
          >
            <KeyValuePairs columns={1} items={errorItems} />
          </Container>
          <Box textAlign="right">
            <Button onClick={handleBackToEdit} data-testid="claim-submitted-back-to-edit-button">
              Back to edit
            </Button>
          </Box>
        </SpaceBetween>
      </Box>
    );
  }

  if (submissionStatus === "success") {
    const claimDetails = buildClaimOutputPairs(submittedResponse);

    return (
      <ContentLayout
        header={
          <SpaceBetween size="m">
            <Header variant="h1">
              <div title="Claim Submitted Header">Claim Submitted</div>
            </Header>
          </SpaceBetween>
        }
      >
        <Container>
          <SpaceBetween size="xxl" direction="vertical">
            <Alert type="success" header="Success!" data-testid="claim-submitted-success-alert">
              <span role="alert">
                Your claim has been successfully submitted! Please see the details below. You will
                be receiving a confirmation email shortly.
              </span>
            </Alert>
            <Container
              data-testid="claim-submitted-success-data-grid"
              header={
                <Header variant="h2" data-testid="data-grid-header">
                  Claim Information
                </Header>
              }
            >
              <KeyValuePairs columns={1} items={claimDetails} />
            </Container>
            <Box textAlign="right">
              <Button
                variant="normal"
                onClick={handleStartNewClaim}
                data-testid="claim-submitted-start-new-claim-button"
              >
                Start a New Claim
              </Button>
            </Box>
          </SpaceBetween>
        </Container>
      </ContentLayout>
    );
  }

  return (
    <CloudscapeJsonForms
      schema={schemaClone}
      data={formState}
      showRequiredIndicator
      spaceBetween="l"
      onChange={handleChange}
      onSubmit={handleSubmit}
      secondaryActions={
        <Button variant="normal" onClick={handleStartNewForm} data-testid="start-new-form-button">
          Start new form
        </Button>
      }
    />
  );
}

export default SchemaFormWithSubmission;
